import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Modal } from './Modal'
import { useBackButtonClose } from '../hooks/useBackButtonClose'

export interface ConfirmOptions {
  /** Dialog title. Default 'Are you sure?'. */
  title?: string
  /** Body message. Can be a string or arbitrary ReactNode. */
  message: ReactNode
  /** Confirm button label. Default 'Confirm'. */
  confirmLabel?: string
  /** Cancel button label. Default 'Cancel'. */
  cancelLabel?: string
  /** When true, the confirm button is styled as a destructive action (red). */
  danger?: boolean
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (value: boolean) => void
}

export interface ConfirmProviderProps {
  children: ReactNode
}

/**
 * Provides the `useConfirm` imperative API. Renders a single shared
 * confirmation dialog at the end of the provider tree so any descendant
 * can request a confirmation via a Promise-returning function.
 */
export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  // Guards against a second call while a dialog is already open — the
  // caller gets queued instead of racing with the currently-open dialog.
  const queueRef = useRef<PendingConfirm[]>([])

  const openNext = useCallback(() => {
    const next = queueRef.current.shift()
    setPending(next ?? null)
  }, [])

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        const entry: PendingConfirm = { options, resolve }
        setPending((current) => {
          if (current) {
            queueRef.current.push(entry)
            return current
          }
          return entry
        })
      }),
    []
  )

  const resolveCurrent = useCallback(
    (value: boolean) => {
      setPending((current) => {
        current?.resolve(value)
        return null
      })
      // After closing, open the next queued dialog (if any) on the next tick
      // so the Modal gets a chance to unmount first.
      setTimeout(openNext, 0)
    },
    [openNext]
  )

  const handleCancel = useCallback(() => resolveCurrent(false), [resolveCurrent])
  const handleConfirm = useCallback(
    () => resolveCurrent(true),
    [resolveCurrent]
  )

  // Back button cancels a live confirm dialog — treated as the same
  // outcome as clicking Cancel or pressing Escape, so the caller's
  // Promise resolves `false`.
  useBackButtonClose(pending !== null, handleCancel)

  const options = pending?.options
  const title = options?.title ?? 'Are you sure?'
  const confirmLabel = options?.confirmLabel ?? 'Confirm'
  const cancelLabel = options?.cancelLabel ?? 'Cancel'
  const danger = options?.danger ?? false

  const value = useMemo(() => confirm, [confirm])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={pending !== null}
        onClose={handleCancel}
        title={title}
        size="sm"
      >
        <div className="space-y-5">
          <div className="text-sm text-text-secondary leading-relaxed">
            {options?.message}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm rounded-md border border-border-primary text-text-primary hover:bg-bg-tertiary transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
                danger
                  ? 'bg-accent-danger text-white hover:bg-red-600'
                  : 'bg-accent-primary text-bg-primary hover:bg-accent-hover'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  )
}

/**
 * Imperative confirmation hook. Call the returned function with options
 * to show a dialog; the Promise resolves `true` on confirm, `false` on
 * cancel (including Escape and backdrop-click dismissal).
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within a <ConfirmProvider>')
  }
  return ctx
}
